import { collection, doc, getDoc, getDocs, query, where, updateDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initFirebase } from '../lib/firebase';
import type { ActivityType, TenantFeatures, TenantTier } from '../types';
import { ensureFreshToken } from '../lib/ensure-fresh-token';

/**
 * Tenant interface
 */
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  settings?: {
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>; // section -> subSection -> order
  };
  features?: TenantFeatures;  // Beta feature flags
  createdAt: Date;
  createdBy?: string;
  // New fields for self-service signup
  description?: string;       // Customer-facing description
  phoneNumber?: string;       // Contact phone number
  openingHours?: string;      // Business hours (free text)
  tier: TenantTier;           // Subscription tier (free/premium)
  maxMarkets: number;         // Maximum markets allowed (3 for free tier)
  // Absence period - tenant is unavailable during this period
  absenceStart?: string;      // YYYY-MM-DD format (inclusive)
  absenceEnd?: string;        // YYYY-MM-DD format (inclusive)
  // Activity types (merchant classification)
  activityTypes?: ActivityType[];
  // Profile photo URL (Firebase Storage)
  profilePhoto?: string;
}

/**
 * Membership interface
 */
export interface Membership {
  id: string;
  userId: string;
  tenantId: string;
  role: 'owner' | 'admin' | 'staff';
  isActive: boolean;
  invitedBy: string | null;
  joinedAt: Date;
  revokedAt?: Date;
  revokedBy?: string;
}

/**
 * Get tenant by ID
 */
export async function getTenantById(tenantId: string): Promise<Tenant | null> {
  const { db } = initFirebase();
  const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));

  if (!tenantDoc.exists()) {
    return null;
  }

  const data = tenantDoc.data();
  return {
    id: tenantDoc.id,
    name: data.name,
    slug: data.slug,
    isActive: data.isActive,
    settings: data.settings || undefined,
    features: data.features || undefined,
    createdAt: data.createdAt?.toDate() || new Date(),
    createdBy: data.createdBy,
    description: data.description || undefined,
    phoneNumber: data.phoneNumber || undefined,
    openingHours: data.openingHours || undefined,
    tier: data.tier || 'free',           // Default to free for existing tenants
    maxMarkets: data.maxMarkets ?? 3,    // Default to 3 for existing tenants
    absenceStart: data.absenceStart || undefined,
    absenceEnd: data.absenceEnd || undefined,
    activityTypes: data.activityTypes || [],
    profilePhoto: data.profilePhoto || undefined,
  };
}

/**
 * Get tenant by slug
 * Note: This returns the tenant regardless of isActive status.
 * This allows direct URL access to inactive tenants.
 * Use isActive check separately when listing tenants publicly.
 */
export async function getTenantBySlug(slug: string): Promise<Tenant | null> {
  const { db } = initFirebase();
  const tenantsQuery = query(
    collection(db, 'tenants'),
    where('slug', '==', slug)
  );

  const snapshot = await getDocs(tenantsQuery);

  if (snapshot.empty) {
    return null;
  }

  const tenantDoc = snapshot.docs[0];
  const data = tenantDoc.data();
  return {
    id: tenantDoc.id,
    name: data.name,
    slug: data.slug,
    isActive: data.isActive,
    settings: data.settings || undefined,
    features: data.features || undefined,
    createdAt: data.createdAt?.toDate() || new Date(),
    createdBy: data.createdBy,
    description: data.description || undefined,
    phoneNumber: data.phoneNumber || undefined,
    openingHours: data.openingHours || undefined,
    tier: data.tier || 'free',           // Default to free for existing tenants
    maxMarkets: data.maxMarkets ?? 3,    // Default to 3 for existing tenants
    absenceStart: data.absenceStart || undefined,
    absenceEnd: data.absenceEnd || undefined,
    activityTypes: data.activityTypes || [],
    profilePhoto: data.profilePhoto || undefined,
  };
}

/**
 * Get all tenants (for admin purposes)
 */
export async function listTenants(): Promise<Tenant[]> {
  const { db } = initFirebase();
  const tenantsSnapshot = await getDocs(collection(db, 'tenants'));

  return tenantsSnapshot.docs.map((tenantDoc) => {
    const data = tenantDoc.data();
    return {
      id: tenantDoc.id,
      name: data.name,
      slug: data.slug,
      isActive: data.isActive,
      settings: data.settings || undefined,
      features: data.features || undefined,
      createdAt: data.createdAt?.toDate() || new Date(),
      createdBy: data.createdBy,
      description: data.description || undefined,
      phoneNumber: data.phoneNumber || undefined,
      openingHours: data.openingHours || undefined,
      tier: data.tier || 'free',           // Default to free for existing tenants
      maxMarkets: data.maxMarkets ?? 3,    // Default to 3 for existing tenants
      absenceStart: data.absenceStart || undefined,
      absenceEnd: data.absenceEnd || undefined,
      activityTypes: data.activityTypes || [],
      profilePhoto: data.profilePhoto || undefined,
    };
  });
}

/**
 * Get user's memberships
 */
export async function getUserMemberships(userId: string): Promise<Membership[]> {
  const { db } = initFirebase();
  const membershipsQuery = query(
    collection(db, 'memberships'),
    where('userId', '==', userId),
    where('isActive', '==', true)
  );

  const snapshot = await getDocs(membershipsQuery);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      userId: data.userId,
      tenantId: data.tenantId,
      role: data.role,
      isActive: data.isActive,
      invitedBy: data.invitedBy,
      joinedAt: data.joinedAt?.toDate() || new Date(),
      revokedAt: data.revokedAt?.toDate(),
      revokedBy: data.revokedBy,
    };
  });
}

/**
 * Create a new tenant (Cloud Function)
 */
export async function createTenant(name: string, slug: string) {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const createTenantFn = httpsCallable(functions, 'createTenant');

  const result = await createTenantFn({ name, slug });
  return result.data as {
    success: boolean;
    tenantId: string;
    slug: string;
    message: string;
  };
}

/**
 * Invite a user to a tenant (Cloud Function)
 */
export async function inviteUserToTenant(
  tenantId: string,
  userEmail: string,
  role: 'owner' | 'admin' | 'staff'
) {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const createMembershipFn = httpsCallable(functions, 'createMembership');

  const result = await createMembershipFn({ tenantId, userEmail, role });
  return result.data as {
    success: boolean;
    membershipId: string;
    message: string;
  };
}

/**
 * Revoke a membership (Cloud Function)
 */
export async function revokeMembership(membershipId: string) {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const revokeMembershipFn = httpsCallable(functions, 'revokeMembership');

  const result = await revokeMembershipFn({ membershipId });
  return result.data as {
    success: boolean;
    message: string;
  };
}

/**
 * Manually refresh user claims (Cloud Function)
 */
export async function refreshUserClaims() {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const refreshClaimsFn = httpsCallable(functions, 'refreshUserClaims');

  const result = await refreshClaimsFn();
  return result.data as {
    success: boolean;
    message: string;
  };
}

/**
 * Update tenant settings
 * Only admins and superadmins can update
 */
export async function updateTenantSettings(
  tenantId: string,
  settings: {
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>;
  }
): Promise<void> {
  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantRef = doc(db, 'tenants', tenantId);

  await updateDoc(tenantRef, {
    settings: settings,
  });
}

/**
 * Update tenant isActive status
 * Controls whether the tenant is listed on the public merchant portal
 * Only admins and superadmins can update
 */
export async function updateTenantIsActive(
  tenantId: string,
  isActive: boolean
): Promise<void> {
  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantRef = doc(db, 'tenants', tenantId);

  await updateDoc(tenantRef, {
    isActive: isActive,
  });
}

/**
 * Update tenant absence period
 * Set both to null to clear absence
 * Only admins and superadmins can update
 */
export async function updateTenantAbsence(
  tenantId: string,
  absenceStart: string | null,
  absenceEnd: string | null
): Promise<void> {
  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantRef = doc(db, 'tenants', tenantId);

  await updateDoc(tenantRef, {
    absenceStart: absenceStart,
    absenceEnd: absenceEnd,
  });
}

/**
 * Update a specific tenant feature flag
 * Only superadmins can update feature flags
 */
export async function updateTenantFeature(
  tenantId: string,
  feature: keyof TenantFeatures,
  enabled: boolean
): Promise<void> {
  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantRef = doc(db, 'tenants', tenantId);

  await updateDoc(tenantRef, {
    [`features.${feature}`]: enabled,
  });
}

/**
 * Update tenant activity types
 * Only admins and superadmins can update
 */
export async function updateTenantActivityTypes(
  tenantId: string,
  activityTypes: ActivityType[]
): Promise<void> {
  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantRef = doc(db, 'tenants', tenantId);

  await updateDoc(tenantRef, {
    activityTypes: activityTypes,
  });
}

/**
 * Update tenant profile info (description, phoneNumber)
 * Only admins and superadmins can update
 */
export async function updateTenantProfile(
  tenantId: string,
  profile: {
    description?: string | null;
    phoneNumber?: string | null;
  }
): Promise<void> {
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantRef = doc(db, 'tenants', tenantId);

  await updateDoc(tenantRef, {
    description: profile.description ?? null,
    phoneNumber: profile.phoneNumber ?? null,
  });
}

/**
 * Update tenant profile photo URL
 */
export async function updateTenantProfilePhoto(
  tenantId: string,
  profilePhoto: string | null
): Promise<void> {
  await ensureFreshToken();

  const { db } = initFirebase();
  const tenantRef = doc(db, 'tenants', tenantId);

  await updateDoc(tenantRef, { profilePhoto });
}

/**
 * Check slug availability (Cloud Function)
 * Used during onboarding to verify the desired slug is available
 */
export async function checkSlugAvailability(
  slug: string
): Promise<{ available: boolean; reason?: string }> {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const checkSlugFn = httpsCallable<{ slug: string }, { available: boolean; reason?: string }>(
    functions,
    'checkSlugAvailability'
  );

  const result = await checkSlugFn({ slug });
  return result.data;
}

/**
 * Input for self-service tenant creation
 */
export interface CreateTenantSelfServiceInput {
  name: string;
  slug: string;
  description?: string;
  phoneNumber?: string;
  openingHours?: string;
  markets: Array<{ marketId: string }>;
}

/**
 * Response from self-service tenant creation
 */
export interface CreateTenantSelfServiceResponse {
  success: boolean;
  tenantId: string;
  slug: string;
  message: string;
}

/**
 * Create a new tenant via self-service onboarding (Cloud Function)
 * Used during the signup wizard to create tenant, membership, and market associations
 * Only accessible to authenticated users without existing tenant memberships
 */
export async function createTenantSelfService(
  input: CreateTenantSelfServiceInput
): Promise<CreateTenantSelfServiceResponse> {
  const { app } = initFirebase();
  const functions = getFunctions(app, 'europe-west1');
  const createTenantFn = httpsCallable<CreateTenantSelfServiceInput, CreateTenantSelfServiceResponse>(
    functions,
    'createTenantSelfService'
  );

  const result = await createTenantFn(input);
  return result.data;
}

/**
 * Subscribe to real-time updates of a tenant document
 * Returns unsubscribe function
 */
export function onTenantSnapshot(
  tenantId: string,
  callback: (tenant: Tenant | null) => void,
  onError?: (error: Error) => void
): () => void {
  const { db } = initFirebase();
  const tenantRef = doc(db, 'tenants', tenantId);

  return onSnapshot(
    tenantRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        callback({
          id: snapshot.id,
          name: data.name,
          slug: data.slug,
          isActive: data.isActive,
          settings: data.settings,
          features: data.features,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          createdBy: data.createdBy,
          description: data.description,
          phoneNumber: data.phoneNumber,
          openingHours: data.openingHours,
          tier: data.tier || 'free',
          maxMarkets: data.maxMarkets ?? 3,
          absenceStart: data.absenceStart,
          absenceEnd: data.absenceEnd,
          activityTypes: data.activityTypes || [],
          profilePhoto: data.profilePhoto,
        });
      } else {
        callback(null);
      }
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}
